import { API_BASE_URL, DEFAULT_COMPANY_ID } from './config';
import { apiFetch } from './apiFetch';
import type { AIReportResult } from './aiQueue';

/** A persisted AI report row from the `ai_reports` table. */
export type PersistedAIReport = AIReportResult & {
  id: string;
  userId: string;
  period: string;
  inputHash: string | null;
  createdAt: string;
};

export type ReportFreshness = {
  isStale: boolean;
  hasReport: boolean;
  lastReportDate: string | null;
  lastReportHash: string | null;
  currentDataHash: string | null;
  changedSince: string[];          // e.g. ['metrics', '9-box position']
  dataSnapshot: {
    metricCount: number;
    latestMetricAt: string | null;
    psychCount: number;
    latestPsychAt: string | null;
    box9UpdatedAt: string | null;
  };
};

export type DashboardAnalytics = {
  totalUsers: number;
  totalDivisions: number;
  totalAssessmentTemplates: number;
  totalMetricDefinitions: number;
  totalCompetencies: number;
  totalJobs: number;
  openJobs: number;
  totalApplications: number;
  totalHires: number;
  averageMatchScore: number;
};

export async function getDashboardAnalytics(companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/analytics/dashboard/${companyId}`);
  return response.json() as Promise<{ success: boolean; data?: { dashboard: DashboardAnalytics }; error?: string }>;
}

/** Fetch persisted AI reports for a specific user from the DB (most recent first, up to 20). */
export async function listAIReports(companyId: string, userId: string) {
  const response = await apiFetch(
    `${API_BASE_URL}/api/ai-reports?companyId=${companyId}&userId=${userId}`,
  );
  return response.json() as Promise<{
    success: boolean;
    data?: { reports: PersistedAIReport[] };
    error?: string;
  }>;
}

/**
 * Fetch report counts for all users in a company in one query.
 * Returns a map of { [userId]: count } — only users with ≥1 report are included.
 * Used by the Reports page left rail to show counts upfront without clicking.
 */
export async function getReportCounts(companyId: string) {
  const response = await apiFetch(
    `${API_BASE_URL}/api/ai-reports/counts?companyId=${companyId}`,
  );
  return response.json() as Promise<{
    success: boolean;
    data?: { counts: Record<string, number> };
    error?: string;
  }>;
}

/** Check whether an employee's latest report reflects their current data snapshot. */
export async function getReportFreshness(companyId: string, userId: string) {
  const response = await apiFetch(
    `${API_BASE_URL}/api/ai-reports/freshness?companyId=${companyId}&userId=${userId}`,
  );
  return response.json() as Promise<{
    success: boolean;
    data?: ReportFreshness;
    error?: string;
  }>;
}

export type OnboardingStatus = {
  hasDivisions: boolean;
  hasEmployees: boolean;
  hasCompetencyCategories: boolean;
  hasAssessmentTemplates: boolean;
  hasMilestoneTemplates: boolean;
  hasJobPostings: boolean;
};

export type OnboardingStatusResult = {
  status: OnboardingStatus;
  completedCount: number;
  totalCount: number;
  allComplete: boolean;
};

export async function getOnboardingStatus(companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/analytics/onboarding-status/${companyId}`);
  return response.json() as Promise<{ success: boolean; data?: OnboardingStatusResult; error?: string }>;
}

export async function getRecentActivity(companyId = DEFAULT_COMPANY_ID) {
  const response = await apiFetch(`${API_BASE_URL}/api/analytics/activity/${companyId}?limit=8`);
  return response.json() as Promise<{
    success: boolean;
    data?: { activity: Array<{ id: string; type: string; title: string; createdAt: string }> };
    error?: string;
  }>;
}
